/**
 * Константы модуля закупок (procurement).
 */

/**
 * Стадия позиции сорсинга — ось «принято ли решение о закупке».
 *
 * Это НЕ про полноту прайсинга («есть цены / ждём» вычисляется из наличия
 * предложений и не хранится), а про жизненный цикл решения по позиции:
 *
 *   SOURCING        — прицениваемся, собираем предложения;
 *   PRICE_REQUESTED — цена запрошена у поставщика, ждём ответа (ставится вручную;
 *                     подстадия прайсинга — снимается сама при выборе поставщика);
 *   TO_ORDER        — поставщик выбран, решено закупать (ждёт оформления заказа);
 *   ORDERED         — ушла в закупку (терминал для сорсинга; дальше живёт в PO);
 *   DROPPED         — позиция заброшена / отбой («отбой заказа» из Excel клиента).
 */
export const SOURCING_STAGE = {
  SOURCING: 'sourcing',
  PRICE_REQUESTED: 'price_requested',
  TO_ORDER: 'to_order',
  ORDERED: 'ordered',
  DROPPED: 'dropped',
} as const;

export type SourcingStage = (typeof SOURCING_STAGE)[keyof typeof SOURCING_STAGE];

export const SOURCING_STAGE_VALUES: readonly SourcingStage[] = Object.values(SOURCING_STAGE);

export const SOURCING_STAGE_LABELS: Record<SourcingStage, string> = {
  [SOURCING_STAGE.SOURCING]: 'Прицениваемся',
  [SOURCING_STAGE.PRICE_REQUESTED]: 'Запросили цену',
  [SOURCING_STAGE.TO_ORDER]: 'К заказу',
  [SOURCING_STAGE.ORDERED]: 'Ушла в закупку',
  [SOURCING_STAGE.DROPPED]: 'Отбой',
};

/**
 * Статус предложения поставщика (sourcing_offer) — ось «принято ли предложение».
 *
 *   PENDING  — котировка на сравнении (дефолт);
 *   ACCEPTED — принято: поставщика выбрали по позиции. Один ACCEPTED на позицию,
 *              синхронно с selected_supplier_id + stage=TO_ORDER самой позиции.
 */
export const OFFER_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
} as const;

export type OfferStatus = (typeof OFFER_STATUS)[keyof typeof OFFER_STATUS];

export const OFFER_STATUS_VALUES: readonly OfferStatus[] = Object.values(OFFER_STATUS);

export const OFFER_STATUS_LABELS: Record<OfferStatus, string> = {
  [OFFER_STATUS.PENDING]: 'На сравнении',
  [OFFER_STATUS.ACCEPTED]: 'Принято',
};

/**
 * Статус закупки по принятой заявке (sourcing_offer) — ось «прогресс закупки».
 * Заполняется только у принятого оффера (acceptOffer ставит NEW), у остальных null.
 *
 *   NEW               — заявка только создана (первый статус, ждёт размещения);
 *   PLACED            — заявка размещена у поставщика;
 *   INVOICE_RECEIVED  — получен счёт;
 *   IN_TRANSIT        — в пути;
 *   RECEIVED          — получено.
 */
export const PURCHASE_STATUS = {
  NEW: 'new',
  PLACED: 'placed',
  INVOICE_RECEIVED: 'invoice_received',
  IN_TRANSIT: 'in_transit',
  RECEIVED: 'received',
} as const;

export type PurchaseStatus = (typeof PURCHASE_STATUS)[keyof typeof PURCHASE_STATUS];

export const PURCHASE_STATUS_VALUES: readonly PurchaseStatus[] = Object.values(PURCHASE_STATUS);

export const PURCHASE_STATUS_LABELS: Record<PurchaseStatus, string> = {
  [PURCHASE_STATUS.NEW]: 'Новая',
  [PURCHASE_STATUS.PLACED]: 'Заявка размещена',
  [PURCHASE_STATUS.INVOICE_RECEIVED]: 'Счёт получен',
  [PURCHASE_STATUS.IN_TRANSIT]: 'В пути',
  [PURCHASE_STATUS.RECEIVED]: 'Получено',
};

/**
 * Порядок прогресса закупки. Нужен автостатусам: включение заявки в закупку и
 * появление счёта двигают статус только ВПЕРЁД и не откатывают то, что снабженец
 * уже проставил руками (заявка «В пути» не вернётся в «Заявка размещена»,
 * если её перекинули в другую закупку).
 */
export const PURCHASE_STATUS_ORDER: Record<PurchaseStatus, number> = {
  [PURCHASE_STATUS.NEW]: 0,
  [PURCHASE_STATUS.PLACED]: 1,
  [PURCHASE_STATUS.INVOICE_RECEIVED]: 2,
  [PURCHASE_STATUS.IN_TRANSIT]: 3,
  [PURCHASE_STATUS.RECEIVED]: 4,
};

/**
 * Спец-ключ сортировки реестра «Заявки поставщикам»: сначала заявки, ещё не
 * включённые в закупку, внутри группы — новые сверху. Не колонка таблицы:
 * значение для defaultSortField, которое раскрывает в два ORDER BY
 * SourcingOfferRepository.orderBy.
 */
export const UNLINKED_FIRST_SORT = 'unlinked_first';

/**
 * Дальше ли `next` по цепочке статусов, чем текущий статус заявки.
 *
 * `current` — то, что лежит в БД: EnumField отдаёт строку, а у непринятой заявки
 * статуса нет вовсе (null). Пустое и неизвестное значение считаем «раньше
 * любого статуса» — иначе автостатус не сработал бы на заявке без статуса.
 */
export function isPurchaseStatusAhead(
  next: PurchaseStatus,
  current: string | null | undefined
): boolean {
  const order: Record<string, number> = PURCHASE_STATUS_ORDER;
  const currentOrder = current == null ? -1 : (order[current] ?? -1);
  return order[next] > currentOrder;
}

// ============================================================================
// Счета поставщиков и оплаты (accounting-документы закупок)
// ============================================================================

/**
 * Типы accounting-документов, которыми владеет модуль закупок.
 * Коды документов в БД (accounting_document_types.code).
 */
export const PROCUREMENT_DOCTYPE = {
  /** Счёт поставщика (входящий счёт на оплату) */
  SUPPLIER_INVOICE: 'SUPPLIER_INVOICE',
  /** Оплата поставщику (исходящий платёж по счёту SUPPLIER_INVOICE) */
  SUPPLIER_PAYMENT: 'SUPPLIER_PAYMENT',
  /** Заказ на закупку (группа принятых заявок, приходящих одним инвойсом/партией) */
  PURCHASE_ORDER: 'PURCHASE_ORDER',
} as const;

/**
 * Статусы заказа на закупку (тип PURCHASE_ORDER).
 *
 *   OPEN      — формируется: в заказ можно добавлять/убирать заявки;
 *   RECEIVED  — партия получена, заказ закрыт (терминал);
 *   CANCELLED — заказ отменён (терминал).
 */
export const PURCHASE_ORDER_STATUS = {
  OPEN: 'OPEN',
  RECEIVED: 'RECEIVED',
  CANCELLED: 'CANCELLED',
} as const;

export type PurchaseOrderStatus =
  (typeof PURCHASE_ORDER_STATUS)[keyof typeof PURCHASE_ORDER_STATUS];

export const PURCHASE_ORDER_STATUS_LABELS: Record<PurchaseOrderStatus, string> = {
  [PURCHASE_ORDER_STATUS.OPEN]: 'Открыт',
  [PURCHASE_ORDER_STATUS.RECEIVED]: 'Получен',
  [PURCHASE_ORDER_STATUS.CANCELLED]: 'Отменён',
};

/** Статусы жизненного цикла счёта поставщика (тип SUPPLIER_INVOICE). */
/**
 * Настройки модуля закупок (optionService; правятся в «Настройках модулей»).
 * Объявляются в setup модуля со значениями по умолчанию.
 */
export const PROCUREMENT_OPTIONS = {
  /**
   * Создавать расход — денежную привязку-транзакцию счёта к сделке — при
   * добавлении/привязке счёта поставщика в сделке. По умолчанию да. Выключено:
   * счёт привязан к сделке (deal_id), но в «Расходах» сделки не появляется и
   * «Сумма по сделке» не ведётся.
   */
  SUPPLIER_INVOICE_DEAL_EXPENSE: 'SUPPLIER_INVOICE_DEAL_EXPENSE',
} as const;

/** Маршрут реестра платежей (счета поставщиков). */
export const SUPPLIER_INVOICES_REGISTER_URL = '/procurement/supplier-invoices';

/** id таблицы реестра платежей — он же префикс её параметров в URL (поиск, сортировка). */
export const SUPPLIER_INVOICES_TABLE_ID = 'procurement-supplier-invoices_v2';

/**
 * Ссылка на реестр платежей, суженный поиском до одного счёта (поиск по точному
 * id) — для уведомлений и внешних ссылок. Если параметры таблицы в URL однажды
 * поменяются, ссылка просто откроет весь реестр.
 */
export function supplierInvoiceRegisterUrl(invoiceId: number): string {
  const params = new URLSearchParams({
    [`${SUPPLIER_INVOICES_TABLE_ID}_search`]: String(invoiceId),
  });
  return `${SUPPLIER_INVOICES_REGISTER_URL}?${params.toString()}`;
}

export const SUPPLIER_INVOICE_STATUS = {
  /** Создан — черновик */
  CREATED: 'CREATED',
  /** На оплату — отправлен на согласование/оплату */
  FOR_PAYMENT: 'FOR_PAYMENT',
  /** Согласован — проставлена дата оплаты */
  APPROVED: 'APPROVED',
  /** Оплачен частично */
  PAID_PARTIAL: 'PAID_PARTIAL',
  /** Оплачен полностью */
  PAID: 'PAID',
  /** Отклонён */
  DECLINED: 'DECLINED',
} as const;

/**
 * Статусы счёта, в которых разрешено принимать оплату («с На оплату и далее»).
 * Доменный инвариант модуля — используется правилом на связь PAYMENT_FOR.
 */
export const SUPPLIER_INVOICE_PAYABLE_STATUSES: readonly string[] = [
  SUPPLIER_INVOICE_STATUS.FOR_PAYMENT,
  SUPPLIER_INVOICE_STATUS.APPROVED,
  SUPPLIER_INVOICE_STATUS.PAID_PARTIAL,
];

/** Типы связей между документами (accounting_document_references). */
export const DOC_REFERENCE_TYPE = {
  /** Платёж (SUPPLIER_PAYMENT) → счёт (SUPPLIER_INVOICE) */
  PAYMENT_FOR: 'PAYMENT_FOR',
  /** Счёт (SUPPLIER_INVOICE) → заказ на закупку (PURCHASE_ORDER): счёт оплачивает заказ */
  INVOICE_FOR_ORDER: 'INVOICE_FOR_ORDER',
} as const;

/**
 * Сколько позиций с тем же наименованием показываем в подсказке о дубле (findSimilar)
 * и в сводке после «Добавить списком». Найденных может быть больше — точное число
 * приходит отдельно (SourcingItemDuplicate.totalMatches).
 */
export const SIMILAR_ITEMS_LIMIT = 5;

/**
 * Опция модуля: температура ИИ при разборе «Добавить списком»
 * (SourcingItemRepository.bulkCreateFromText). Значение своё у каждого инстанса —
 * опции хранятся в базе инстанса и правятся на экране опций.
 */
export const OPTION_SOURCING_BULK_PARSE_TEMPERATURE = 'PROCUREMENT_SOURCING_BULK_PARSE_TEMPERATURE';

/**
 * Температура разбора по умолчанию — 0,7: столько разбор фактически получал до
 * появления опции (запрошенный 0 askAdvanced превращает в 0,7 — `temperature || 0.7`),
 * поэтому у инстансов без своего значения поведение не меняется.
 * Ноль ставить нельзя, пока это не исправлено в askAdvanced: он тоже станет 0,7.
 */
export const SOURCING_BULK_PARSE_TEMPERATURE_DEFAULT = 0.7;
